import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ExportButton from "@/components/ExportButton";
import EmptyState from "@/components/EmptyState";
import { Clock } from "@phosphor-icons/react";
import { balanceCls } from "@/lib/balanceColor";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const BUCKET_STYLE = {
    b_0_30:    "text-emerald-800 bg-emerald-50/40",
    b_31_60:   "text-amber-800 bg-amber-50/40",
    b_61_90:   "text-orange-800 bg-orange-50/40",
    b_90_plus: "text-rose-800 bg-rose-50/40 font-semibold",
};

export default function AgingReport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/reports/aging").then((r) => setData(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-slate-500" data-testid="aging-loading">Loading…</div>;
    const rows = data?.rows || [];
    const totals = data?.totals || {};

    return (
        <div className="space-y-6" data-testid="aging-report-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Statements</div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Aging Report</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Outstanding balances bucketed by age (FIFO settlement). As of <span className="font-medium">{data?.as_of || "—"}</span>.
                    </p>
                </div>
                <ExportButton
                    endpoint={{ pdf: "/export/aging.pdf", xlsx: "/export/aging.xlsx" }}
                    filenameBase="aging-report"
                    testId="aging-export"
                    variant="primary"
                    label="Download Aging"
                />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <Card label="0-30 days" value={fmt(totals.b_0_30)} accent="emerald" testId="aging-total-0-30" />
                <Card label="31-60 days" value={fmt(totals.b_31_60)} accent="amber" testId="aging-total-31-60" />
                <Card label="61-90 days" value={fmt(totals.b_61_90)} accent="orange" testId="aging-total-61-90" />
                <Card label="90+ days" value={fmt(totals.b_90_plus)} accent="rose" testId="aging-total-90-plus" />
                <Card label="Total outstanding" value={fmt(totals.total)} accent="slate" bold testId="aging-total" />
            </div>

            {rows.length === 0 ? (
                <EmptyState icon={Clock} title="Nothing outstanding" description="All party balances are settled or negative — nothing to age." />
            ) : (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="aging-table">
                            <thead>
                                <tr className="bg-slate-900 text-left text-xs font-semibold uppercase tracking-wider text-white">
                                    <th className="px-4 py-3">Party</th>
                                    <th className="px-4 py-3">Contact</th>
                                    <th className="px-3 py-3 text-right">0-30 days</th>
                                    <th className="px-3 py-3 text-right">31-60 days</th>
                                    <th className="px-3 py-3 text-right">61-90 days</th>
                                    <th className="px-3 py-3 text-right">90+ days</th>
                                    <th className="px-4 py-3 text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.party_id} data-testid={`aging-row-${r.party_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-4 py-3 font-medium text-slate-900">{r.party_name}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">
                                            {r.contact_person}{r.phone ? <> · <span className="tabular-nums">{r.phone}</span></> : null}
                                        </td>
                                        <td className={`px-3 py-3 text-right tabular-nums ${BUCKET_STYLE.b_0_30}`}>{r.b_0_30 ? fmt(r.b_0_30) : ""}</td>
                                        <td className={`px-3 py-3 text-right tabular-nums ${BUCKET_STYLE.b_31_60}`}>{r.b_31_60 ? fmt(r.b_31_60) : ""}</td>
                                        <td className={`px-3 py-3 text-right tabular-nums ${BUCKET_STYLE.b_61_90}`}>{r.b_61_90 ? fmt(r.b_61_90) : ""}</td>
                                        <td className={`px-3 py-3 text-right tabular-nums ${BUCKET_STYLE.b_90_plus}`}>{r.b_90_plus ? fmt(r.b_90_plus) : ""}</td>
                                        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${balanceCls(r.total)}`}>{fmt(r.total)}</td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-slate-900 bg-slate-100 font-semibold">
                                    <td colSpan={2} className="px-4 py-3 uppercase tracking-wider text-xs text-slate-700">Totals</td>
                                    <td className="px-3 py-3 text-right tabular-nums text-emerald-800">{fmt(totals.b_0_30)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums text-amber-800">{fmt(totals.b_31_60)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums text-orange-800">{fmt(totals.b_61_90)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums text-rose-800">{fmt(totals.b_90_plus)}</td>
                                    <td className="px-4 py-3 text-right tabular-nums text-slate-900">{fmt(totals.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function Card({ label, value, accent, bold, testId }) {
    const tone = {
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        orange: "text-orange-700",
        rose: "text-rose-700",
        slate: "text-slate-900",
    }[accent] || "text-slate-900";
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className={`mt-2 font-display text-xl tabular-nums ${bold ? "font-bold" : "font-semibold"} ${tone}`}>{value}</div>
        </div>
    );
}
